import { useState } from 'react'
import { api } from '../api/client'
import { useAuth } from '../store/auth'
import { useSettings } from '../store/settings'

export function LoginGate({ children }: { children: React.ReactNode }) {
  const session = useAuth((s) => s.session)
  const setSession = useAuth((s) => s.setSession)
  const service = useSettings((s) => s.service)
  const setService = useSettings((s) => s.setService)
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (session) return <>{children}</>

  async function login(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      const s = await api.login({
        service,
        identifier: identifier.trim(),
        password: password.trim(),
      })
      setSession(s)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[200] grid place-items-center p-4">
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(1000px 500px at 50% 30%, rgba(56,189,248,0.18), transparent 60%), linear-gradient(180deg, #060a14, #0a1024)',
        }}
      />
      <div className="glass-strong relative w-full max-w-md p-6">
        <div className="mb-1 flex items-center gap-2">
          <div className="grid h-9 w-9 place-items-center rounded-xl border border-white/10 bg-gradient-to-br from-sky-400/40 to-violet-500/40 text-base font-black">
            b
          </div>
          <h1 className="text-lg font-extrabold tracking-tight">
            bskydisc<span className="accent-text">o</span>mbobulate
          </h1>
        </div>
        <p className="mt-2 text-sm text-ink-200">
          Sign in with a Bluesky <strong>app password</strong> to start. Credentials are stored
          locally in your browser only — nothing leaves your machine except the auth call to
          the ATProto service.
        </p>

        <form className="mt-5 flex flex-col gap-3" onSubmit={login}>
          <label className="text-xs uppercase tracking-widest text-ink-300">
            ATProto service
            <input
              className="glass-input mt-1 w-full"
              value={service}
              onChange={(e) => setService(e.target.value)}
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
            />
          </label>
          <label className="text-xs uppercase tracking-widest text-ink-300">
            handle or email
            <input
              className="glass-input mt-1 w-full"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              autoComplete="username"
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
              placeholder="you.bsky.social"
              required
            />
          </label>
          <label className="text-xs uppercase tracking-widest text-ink-300">
            app password
            <input
              className="glass-input mt-1 w-full"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              placeholder="xxxx-xxxx-xxxx-xxxx"
              required
            />
          </label>

          {error ? (
            <div className="rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-100">
              {error}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={busy || !identifier || !password}
            className="glass-button glass-button--accent mt-1 w-full justify-center disabled:opacity-50"
          >
            {busy ? 'Signing in…' : 'Sign in'}
          </button>

          <p className="text-[11px] text-ink-300">
            Don't have an app password? Generate one at{' '}
            <a
              className="accent-text underline"
              href="https://bsky.app/settings/app-passwords"
              target="_blank"
              rel="noreferrer"
            >
              bsky.app/settings/app-passwords
            </a>
            . Never paste your real account password.
          </p>
        </form>
      </div>
    </div>
  )
}
